import * as _ from 'lodash'
import { Observable, of, forkJoin, zip, from } from 'rxjs'
import { catchError, first, map, switchMap, tap } from 'rxjs/operators'

import { RecordMaskResource, MetaRecordMaskResource, MediaMetaDataResource, MediaUploadResult2, Wemi } from '@skinsoft/types'
import { EditScreenVCSaveResult, EditScreenViewControllerInterface } from '@skinsoft/classes-interfaces-definition'
import { MaskFieldUpdateEvent, MaskFieldUpdateEventType } from '@skinsoft/record-mask/src/components/mask-field'
import { ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import { WemiUtils } from '@skinsoft/wemi-utils'

declare const config
const FIND_MEDIAS_MAX_TRY = config.editScreen.mediaUpload.maxAttempts
const FIND_MEDIAS_REPEAT_DELAY = config.editScreen.mediaUpload.retryDelay

export class ItemEditScreenController implements EditScreenViewControllerInterface<Wemi> {
    private imageTransferHelper: ImageTransferHelper
    private mediaTransferHelper: MediaTransferHelper
    private originalItemIdentifier: string | null = null

    constructor(private on409?: () => void) {
        this.imageTransferHelper = ImageTransferHelper.getInstance()
        this.mediaTransferHelper = MediaTransferHelper.getInstance()
    }

    getEntity(itemId: string): Observable<Wemi> {
        const wemi$ = window.navigator.onLine
            ? WemiUtils.getWemiFromItem(itemId)
            : WemiUtils.getOrCreateWemiFromItemOffline(itemId)

        return wemi$.pipe(
            first(),
            map(wemi => {
                const cloned = _.cloneDeep(wemi)
                this.originalItemIdentifier = _.get(cloned, 'item.identificationItemSchema.identifiers[0].identifier', null)
                return cloned
            })
        )
    }

    adaptValuesBeforeSave(wemi: Wemi, _recordMasks: (RecordMaskResource | MetaRecordMaskResource)[]) {
        WemiUtils.removeEmptyObjectsFromWemi(wemi)
    }

    saveEntity(wemi: Wemi, recordMasks: (RecordMaskResource | MetaRecordMaskResource)[]): Observable<EditScreenVCSaveResult> {
        this.adaptValuesBeforeSave(wemi, recordMasks)

        const isOffline = !window.navigator.onLine || !!wemi.item?.id?.startsWith('_')

        if (isOffline && wemi.item?.identificationItemSchema?.identifiers) {
            wemi.item.identificationItemSchema.identifiers[0].identifier = this.originalItemIdentifier ?? undefined
        }

        const uploadImages$ = window.navigator.onLine ? this.uploadImages(wemi) : of([] as MediaUploadResult2[])

        return uploadImages$.pipe(
            map(mediaUploadResults => {
                const saveResult: EditScreenVCSaveResult = {
                    success: true,
                    mediaUploadResult: mediaUploadResults,
                }
                for (const uploadResult of mediaUploadResults) {
                    if (uploadResult.cacheKey && uploadResult.mediaId) {
                        this.updateMediaId(wemi, uploadResult.cacheKey, uploadResult.mediaId)
                    } else {
                        saveResult.success = false
                    }
                }
                return saveResult
            }),
            switchMap(saveResult => {
                if (!saveResult.success) {
                    return of(saveResult)
                }
                const save$: Promise<any> = isOffline
                    ? WemiUtils.saveWemiOffline(wemi)
                    : WemiUtils.saveWemi(wemi)

                return from(save$).pipe(
                    map(() => saveResult),
                    catchError(error => {
                        if (error.status === 409 && this.on409) {
                            this.on409()
                        }
                        saveResult.success = false
                        return of(saveResult)
                    })
                )
            })
        )
    }

    uploadImage(wemi: Wemi, mediaCacheKey: string): Observable<MediaUploadResult2> {
        const mediaId = this.mediaTransferHelper.getMediaIdFromCacheKey(mediaCacheKey)

        if (mediaId) {
            this.updateMediaId(wemi, mediaCacheKey, mediaId)
            return of({ cacheKey: mediaCacheKey, mediaId } as MediaUploadResult2)
        }

        return this.imageTransferHelper.uploadLocalImageToApi(mediaCacheKey).pipe(
            catchError(() => of({ cacheKey: mediaCacheKey, errorOccured: true, timeOut: false } as MediaUploadResult2)),
            switchMap(uploadResult => {
                if (uploadResult.fileName && !uploadResult.errorOccured) {
                    return zip(
                        this.mediaTransferHelper.repeatFindingMediaIdFromFileNameWithLimit(
                            uploadResult.fileName, FIND_MEDIAS_MAX_TRY, FIND_MEDIAS_REPEAT_DELAY,
                        ),
                        of(uploadResult),
                    )
                }
                uploadResult.errorOccured = true
                return zip(of(null), of(uploadResult))
            }),
            map(([media, uploadResult]) => {
                if (!uploadResult.errorOccured) {
                    if (media?.id) {
                        uploadResult.mediaId = media.id
                    } else {
                        uploadResult.timeOut = true
                        uploadResult.errorOccured = true
                    }
                }
                return uploadResult
            }),
            tap(uploadResult => {
                if (uploadResult.mediaId && uploadResult.cacheKey) {
                    this.mediaTransferHelper.saveMappingCacheKeyMediaId(uploadResult.cacheKey, uploadResult.mediaId)
                    this.updateMediaId(wemi, uploadResult.cacheKey, uploadResult.mediaId)
                }
            })
        )
    }

    private uploadImages(wemi: Wemi): Observable<MediaUploadResult2[]> {
        const cachedKeys = this.getCachedMediasKeys(wemi)
        if (!cachedKeys.length) return of([])

        const uploads$: Observable<MediaUploadResult2>[] = []
        for (const cacheKey of cachedKeys) {
            const mediaId = this.mediaTransferHelper.getMediaIdFromCacheKey(cacheKey)
            if (mediaId) {
                this.updateMediaId(wemi, cacheKey, mediaId)
            } else {
                uploads$.push(
                    this.imageTransferHelper.uploadLocalImageToApi(cacheKey).pipe(
                        catchError(() => of({ cacheKey, errorOccured: true, timeOut: false } as MediaUploadResult2))
                    )
                )
            }
        }

        if (!uploads$.length) return of([])

        return forkJoin(uploads$).pipe(
            switchMap(allResults => {
                const [withNames, noNames] = _.partition(allResults, r => r.fileName)

                for (const r of allResults) {
                    if (noNames.find(n => n.cacheKey === r.cacheKey)) {
                        r.errorOccured = true
                        r.timeOut = false
                    }
                }

                const fileNames = withNames.map(r => r.fileName!)
                if (fileNames.length) {
                    return zip(
                        this.mediaTransferHelper.repeatFindingMediasIdsFromFileNamesWithLimit(
                            fileNames, FIND_MEDIAS_MAX_TRY, FIND_MEDIAS_REPEAT_DELAY,
                        ),
                        of(allResults),
                    )
                }
                return zip(of([] as MediaMetaDataResource[]), of(allResults))
            }),
            switchMap(([mediasMetaData, results]) => {
                for (const r of results) {
                    const meta = mediasMetaData.find(m => m.metaData?.fileRecordCS?.fileName === r.fileName)
                    if (meta) {
                        r.mediaId = meta.id
                        if (r.cacheKey) {
                            this.mediaTransferHelper.saveMappingCacheKeyMediaId(r.cacheKey, meta.id)
                        }
                    } else if (!r.errorOccured) {
                        r.errorOccured = true
                        r.timeOut = true
                    }
                }
                return of(results)
            })
        )
    }

    private getCachedMediasKeys(wemi: Wemi): string[] {
        const res: string[] = []
        const assocMedias = wemi?.item?.associatedMedias
        if (assocMedias) {
            if (assocMedias.first && MediaTransferHelper.isLocalMedia(assocMedias.first)) res.push(assocMedias.first)
            if (assocMedias.second && MediaTransferHelper.isLocalMedia(assocMedias.second)) res.push(assocMedias.second)
            if (assocMedias.third && MediaTransferHelper.isLocalMedia(assocMedias.third)) res.push(assocMedias.third)
            if (assocMedias.medias?.length) {
                for (const id of assocMedias.medias) {
                    if (MediaTransferHelper.isLocalMedia(id)) res.push(id)
                }
            }
        }
        return _.uniq(res)
    }

    private updateMediaId(wemi: Wemi, oldId: string, newId: string) {
        const assocMedias = wemi?.item?.associatedMedias
        if (!assocMedias) return
        if (assocMedias.first === oldId) assocMedias.first = newId
        if (assocMedias.second === oldId) assocMedias.second = newId
        if (assocMedias.third === oldId) assocMedias.third = newId
        if (assocMedias.medias?.length) {
            assocMedias.medias = assocMedias.medias.map(id => id === oldId ? newId : id)
        }
    }

    updateEntity(event: MaskFieldUpdateEvent, wemi: Partial<Wemi>) {
        const subWemi = (event as any).subWemi
        const fullPath = subWemi ? `${subWemi}.${event.fieldPath}` : event.fieldPath

        if (event.type === MaskFieldUpdateEventType.DELETE) {
            const lastPropRegex = /(\[\d\]|\.[a-zA-Z\d]*)$/
            const pathToParent = fullPath.replace(lastPropRegex, '')
            const lastParent = _.get(wemi, pathToParent)

            if (_.isArray(lastParent)) {
                const matches = fullPath.match(lastPropRegex)
                if (matches?.length) {
                    // @ts-ignore need to upgrade ts (replaceAll)
                    const indexStr = matches[0].replaceAll(/\[|\]|\./g, '')
                    const index = parseInt(indexStr)
                    if (!isNaN(index)) lastParent.splice(index, 1)
                }
            } else {
                _.unset(wemi, fullPath)
            }
        } else {
            _.set(wemi, fullPath, event.value)
        }
    }

    addImage(mediaId: string, wemi: Partial<Wemi>) {
        if (!_.get(wemi, 'item.associatedMedias')) {
            _.set(wemi, 'item.associatedMedias', {})
        }
        const medias: string[] = _.get(wemi, 'item.associatedMedias.medias', []) ?? []
        if (!medias.includes(mediaId)) {
            if (_.get(wemi, 'item.associatedMedias.medias')) {
                medias.push(mediaId)
            } else {
                _.set(wemi, 'item.associatedMedias.medias', [mediaId])
            }
        }
    }

    deleteImage(mediaId: string, wemi: Partial<Wemi>) {
        const assocMedias = (wemi as Wemi)?.item?.associatedMedias
        if (mediaId && assocMedias?.medias?.length) {
            assocMedias.medias = assocMedias.medias.filter(id => id !== mediaId)
        }
    }

    setFirstMedia(mediaId: string, wemi: Partial<Wemi>) {
        if (mediaId) {
            this.addImage(mediaId, wemi)
            _.set(wemi, 'item.associatedMedias.first', mediaId)
        }
    }
}
